/**
 * All user-facing copy for the Schedule Interview wizard.
 * Centralised here so PM can review and update text without digging through components.
 *
 * Usage:
 *   import { SCHEDULE_INTERVIEW_COPY } from './copy';
 *   SCHEDULE_INTERVIEW_COPY.header.create
 */

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------

const header = {
  create: 'Set up the interview for',
  createSelfSchedule: 'Select self-schedule slots for',
  edit: 'Edit the interview for',
  editSelfSchedule: 'Edit self-schedule slots for',
  /** Used when the confirmed-interview card picks the "Send additional
   *  slots" menu item — the wizard opens on step 1 prefilled from the
   *  source schedule. */
  sendAdditionalSlots: 'Send additional slots to',
} as const;

// ---------------------------------------------------------------------------
// Steps
// ---------------------------------------------------------------------------

const steps = {
  details: 'Details',
  slotSelectionEmail: 'Slot selection email',
  communication: 'Communication',
  scorecard: 'Scorecard',
} as const;

// ---------------------------------------------------------------------------
// Footer
// ---------------------------------------------------------------------------

const footer = {
  cancel: 'Cancel',
  back: 'Back',
  scheduleNow: 'Schedule interview now',
  scheduleInterview: 'Schedule interview',
  saveInterview: 'Save interview',
  saving: 'Saving…',
  scheduling: 'Scheduling…',
  nextCommunication: 'Next: Communication',
  nextSlotSelectionEmail: 'Next: Slot selection email',
  nextScorecard: 'Next: Scorecard',
} as const;

// ---------------------------------------------------------------------------
// Details step
// ---------------------------------------------------------------------------

const details = {
  sectionLabel: 'Interview details',
  scheduleSectionLabel: 'Schedule details',
  interviewName: {
    label: 'Interview name',
    placeholder: 'Add interview name here',
  },
  interviewType: {
    label: 'Interview type',
  },
  interviewer: {
    label: 'Interviewer',
    labelPlural: 'Interviewers',
    placeholder: 'Search interviewers',
    recommended: 'Recommended',
    recommendedDescription: 'Based on interview load and availability',
    clickToSelect: 'CLICK TO SELECT THIS INTERVIEWER',
    or: 'OR',
    multipleInterviewersInfo: 'Multiple interviewers in one-on-one interviews',
    multipleInterviewersDescription:
      'Since you are sharing the availability of multiple interviewers, one interviewer will be selected based on the time slot picked by the candidate.',
  },
  meetingRoom: {
    label: 'Meeting room',
    checkboxLabel: 'Book a meeting room',
    requiredError: 'Please select a meeting room',
    search: 'Search',
    available: 'Available',
    busy: 'Busy',
  },
  calendar: {
    label: 'Calendar',
  },
  duration: {
    label: 'Duration',
    unit: 'mins',
  },
  calendarVisibility: {
    label: 'Make the interview private',
    subLabel: 'Hide calendar event details from non-attendees',
    // SCH-70 — Outlook only honours a private event when the API caller
    // owns the target calendar (Microsoft Graph silently drops the flag
    // otherwise, so we send `default`). Product uses one message for both
    // locked cases: a shared calendar you don't own, and self-schedule.
    lockedSharedCalendar:
      'Only owners can mark events as private on Outlook shared calendars',
    lockedSelfSchedule:
      'Only owners can mark events as private on Outlook shared calendars',
  },
  location: {
    label: 'Location',
  },
  host: {
    label: 'Host',
  },
  noteTaker: {
    label: 'Enable AI notetaker?',
    description:
      'The AI Notetaker automatically captures key points and summaries during meetings. It organizes and highlights important information for easy review and follow-up.',
  },
  findTime: {
    label: 'Find time this week',
    optionsSelected: 'options selected',
    preview: 'Preview',
    clear: 'Clear',
    nextWeekday: 'Next weekday',
    nextNWeekdays: (n: number) => `Next ${n} weekdays`,
    nextWeek: 'Next week',
    nextNWeeks: (n: number) => `Next ${n} weeks`,
    minNotice: (hours: number) =>
      `Note: Candidate can only book interviews starting ${hours} hours from now. Select slots beyond this timeframe to give candidate options.`,
  },
  candidatePhone: {
    label: (name: string) => `${name}'s phone`,
  },
} as const;

// ---------------------------------------------------------------------------
// Slot selection email step (self-schedule only)
// ---------------------------------------------------------------------------

const slotSelectionEmail = {
  placeholders: {
    template: 'Select a template',
    from: 'Select from',
    to: 'Candidate email',
    subject: 'Book a slot with {{org.name}}',
  },
} as const;

// ---------------------------------------------------------------------------
// Communication step
// ---------------------------------------------------------------------------

const communication = {
  organizerSender: {
    label: "Organizer's email",
  },
  candidate: {
    calendarTitle: "Candidate's calendar invite",
    emailTitle: "Candidate's email",
  },
  interviewer: {
    calendarTitle: "Interviewer's calendar invite",
    emailTitle: "Interviewer's email",
  },
  placeholders: {
    template: 'Select a template',
    from: 'Select from',
    subject: 'Enter subject',
  },
  icsCheckbox: 'Include calendar invite as an attachment (.ics file)',
  emailSentBadge: (count: number) =>
    `${count} email${count === 1 ? '' : 's'} sent`,
} as const;

// ---------------------------------------------------------------------------
// Scorecard step
// ---------------------------------------------------------------------------

const scorecard = {
  enableLabel: 'Enable scorecards for this interview',
  aiLabel: 'Allow interviewers to complete scorecards with AI?',
  aiDescription:
    'Helps draft scorecard faster, improving completion rates and consistency',
  templatePlaceholder: 'Select template',
  addQuestion: 'Add question',
  templateNote:
    'Note: This is the recommended scorecard template. You can customize it but changes will only apply to this interview.',
} as const;

// ---------------------------------------------------------------------------
// Edit mode — confirmation dialogs
// ---------------------------------------------------------------------------

// Title / label builders pluralize their nouns by recipient count so
// flows with a single candidate (no interviewers, or just one)
// don't read as "updated emails will be sent to: candidate" when
// only one email is going out.
const invite = (n: number) => (n === 1 ? 'invite' : 'invites');
const email = (n: number) => (n === 1 ? 'email' : 'emails');

const editDialogs = {
  saveInvites: {
    title: (n: number) => `Save and send updated ${invite(n)}?`,
    invitesLabel: (n: number) => `Updated ${invite(n)} will be sent to:`,
  },
  saveEmails: {
    title: (n: number) => `Save and send updated ${email(n)}?`,
    emailsLabel: (n: number) => `Updated ${email(n)} will be sent to:`,
  },
  saveInvitesAndEmails: {
    title: (i: number, e: number) =>
      `Save and send updated ${invite(i)} and ${email(e)}?`,
  },
  candidateLabel: 'Candidate:',
  interviewerLabel: (n: number) => (n === 1 ? 'Interviewer:' : 'Interviewers:'),
} as const;

// ---------------------------------------------------------------------------
// Toasts
// ---------------------------------------------------------------------------

const toast = {
  createSuccess: { title: 'Interview scheduled' },
  updateSuccess: { title: 'Interview updated' },
  /** Self-schedule create — "interview scheduled" reads like the
   *  interview is booked, but at this point we've only sent slot
   *  options to the candidate. */
  selfScheduleCreateSuccess: { title: 'Self schedule sent' },
  /** Self-schedule edit (pending OR confirmed) — same reason. */
  selfScheduleUpdateSuccess: { title: 'Self schedule updated' },
  cancelSuccess: { title: 'Interview cancelled' },
  saveError: {
    title: 'Could not save interview',
    fallback: 'Please try again.',
  },
} as const;

// ---------------------------------------------------------------------------
// Confirmed-interview menu (ScheduledInterview.tsx)
// ---------------------------------------------------------------------------

const scheduledInterviewMenu = {
  /** Used for every "Edit" action on a confirmed interview — manual or
   *  self-schedule-sourced. Previously self-schedule swapped in
   *  "Edit scheduled interview"; product asked to drop the variant
   *  and use the shorter label consistently. */
  editInterview: 'Edit interview',
  sendAdditionalSlots: {
    label: 'Send additional slots',
    /** Sub-text on the menu item. For an interview that is already
     *  completed OR marked candidate-no-show, sending new slots closes
     *  out the existing booking as completed (not canceled) — only a
     *  still-upcoming interview gets canceled. */
    description: (isCompleted: boolean, isNoShow: boolean) =>
      isCompleted || isNoShow
        ? 'The completed interview will be canceled'
        : 'The scheduled interview will be canceled',
  },
} as const;

// ---------------------------------------------------------------------------
// Single export
// ---------------------------------------------------------------------------

export const SCHEDULE_INTERVIEW_COPY = {
  header,
  steps,
  footer,
  details,
  slotSelectionEmail,
  communication,
  scorecard,
  editDialogs,
  scheduledInterviewMenu,
  toast,
} as const;
